import { useMemo, useState } from 'react'
import { CheckCircle, Copy, Eye, PencilSimple, Plus, Prohibit, Tag } from '@phosphor-icons/react'
import {
  Banner,
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Select,
  TextArea,
  type Column,
} from '@/ui'
import { errorMessage } from '../../api/core'
import {
  useAllSkus,
  useApproveLabel,
  useLabelVersion,
  useLabelVersions,
  usePackingHalls,
  useRetireLabel,
  useSaveLabel,
  type LabelVersionRow,
  type PackingHall,
  type SkuRow,
} from '../../api/packFull'
import { ActionButton } from '../../components/Can'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { DetailDrawer } from '../../components/DetailDrawer'
import { FilterBar, SelectFilter } from '../../components/FilterBar'
import { FormDialog } from '../../components/FormDialog'
import { ConsolePage } from '../../components/Layout'
import { Code, Facts, Money, Status, UserName, When } from '../../components/bits'
import { LabelPreview, type LabelPreviewData } from '../../components/pack/LabelPreview'
import { RefusalBanner } from '../../components/pack/Refusal'
import { useUrlState } from '../../lib/useUrlState'
import { parseRupeesToPaise } from '@/lib/money'
import {
  approvalProblems,
  claimWordsProblem,
  createLabelBody,
  draftCopyOf,
  draftFromLabel,
  emptyLabelDraft,
  labelOk,
  updateLabelBody,
  validateLabelDraft,
  type LabelDraft,
} from '../../model/pack/labelForm'
import { labelStatusText } from '../../model/pack/packWords'

const LIMIT = 50

type Editing = { mode: 'create'; draft?: LabelDraft } | { mode: 'edit'; row: LabelVersionRow }

const PANEL_HINTS = ['PESTICIDE', 'AFLATOXIN', 'HEAVY_METALS']

function previewOf(l: LabelVersionRow, sku: SkuRow | undefined): LabelPreviewData {
  return {
    productName: l.skuName,
    netG: sku?.netG ?? null,
    mrpPaise: l.mrpPaise,
    shelfLifeDays: sku?.shelfLifeDays ?? null,
    fssaiLicenceNo: l.fssaiLicenceNo,
    allergenStatement: l.allergenStatement,
    claimText: l.claimText,
    hallName: l.premisesName,
  }
}

const problemsOf = (l: LabelVersionRow) =>
  approvalProblems({ ...l, hallLicenceNo: l.fssaiLicenceNo })

/** Label versions (F7.4): what each product's label prints at a hall; approved once, never edited. */
export function LabelsPage() {
  const [f, setF, { reset, active }] = useUrlState({
    sku: '',
    status: '',
    hall: '',
    open: '',
    page: 1,
  })
  const q = useLabelVersions({
    skuId: f.sku || undefined,
    status: f.status || undefined,
    premisesLocationId: f.hall || undefined,
    page: f.page,
    limit: LIMIT,
  })
  const skus = useAllSkus()
  const halls = usePackingHalls()
  const skuById = useMemo(() => new Map((skus.data ?? []).map((s) => [s.id, s])), [skus.data])
  const [editing, setEditing] = useState<Editing | null>(null)
  const [approving, setApproving] = useState<LabelVersionRow | null>(null)
  const [retiring, setRetiring] = useState<LabelVersionRow | null>(null)
  const rows = q.data?.rows ?? []
  const inPage = rows.find((r) => r.id === f.open) ?? null
  const one = useLabelVersion(f.open && !inPage && !q.isLoading ? f.open : null)
  const opened = inPage ?? (f.open ? (one.data ?? null) : null)

  const actionsFor = (r: LabelVersionRow, size: 'sm' | 'md' = 'sm') => (
    <>
      {r.status === 'DRAFT' && (
        <>
          <ActionButton
            perm="label-versions:update"
            hide
            size={size}
            variant="ghost"
            leadingIcon={<PencilSimple />}
            onClick={() => setEditing({ mode: 'edit', row: r })}
          >
            Edit
          </ActionButton>
          <ActionButton
            perm="label-versions:approve"
            hide
            size={size}
            variant="secondary"
            leadingIcon={<CheckCircle />}
            onClick={() => setApproving(r)}
          >
            Approve
          </ActionButton>
        </>
      )}
      {r.status !== 'DRAFT' && (
        <ActionButton
          perm="label-versions:create"
          hide
          size={size}
          variant="ghost"
          leadingIcon={<Copy />}
          onClick={() => setEditing({ mode: 'create', draft: draftCopyOf(r) })}
        >
          New version
        </ActionButton>
      )}
      {r.status !== 'RETIRED' && (
        <ActionButton
          perm="label-versions:update"
          hide
          size={size}
          variant="ghost"
          leadingIcon={<Prohibit />}
          onClick={() => setRetiring(r)}
        >
          {r.status === 'DRAFT' ? 'Withdraw draft' : 'Retire'}
        </ActionButton>
      )}
    </>
  )

  const columns: Column<LabelVersionRow>[] = [
    { id: 'code', header: 'Version', cell: (r) => <Code>{r.code}</Code> },
    {
      id: 'sku',
      header: 'Product',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{r.skuName}</span>
          <span className="font-mono text-[12px] text-muted">{r.skuCode}</span>
        </span>
      ),
    },
    {
      id: 'hall',
      header: 'Packing hall',
      hideBelow: 'md',
      cell: (r) => r.premisesName ?? r.premisesCode ?? '—',
    },
    { id: 'mrp', header: 'MRP', align: 'right', cell: (r) => <Money paise={r.mrpPaise} /> },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => <Status status={r.status} label={labelStatusText(r.status)} />,
    },
    {
      id: 'ready',
      header: 'Approval',
      hideBelow: 'lg',
      cell: (r) => {
        if (r.status !== 'DRAFT')
          return r.approvedAt ? (
            <When at={r.approvedAt} dateOnly />
          ) : (
            <span className="text-muted">—</span>
          )
        const n = problemsOf(r).length
        return n === 0 ? (
          <span className="text-ok">Ready to approve</span>
        ) : (
          <span className="text-warn">
            {n} {n === 1 ? 'thing' : 'things'} missing
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Eye />}
            onClick={() => setF({ open: r.id })}
          >
            Preview
          </Button>
          {actionsFor(r)}
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Labels"
      description="Each label version is what a product's pouch prints at one packing hall: MRP, allergen line, the tested-not-detected claim and the hall's FSSAI licence. The quality person approves it once; after that it is never edited — a change is a new version."
      actions={
        <ActionButton
          perm="label-versions:create"
          leadingIcon={<Plus />}
          onClick={() => setEditing({ mode: 'create' })}
        >
          New label
        </ActionButton>
      }
    >
      <DataTable<LabelVersionRow>
        caption="Label versions"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => setF({ open: r.id })}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        empty={
          <EmptyState
            icon={<Tag />}
            title={active ? 'No labels match' : 'No labels yet'}
            description={
              active
                ? 'Try other filters.'
                : 'Add a label for a product at a packing hall, then ask the quality person to approve it.'
            }
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Product"
              value={f.sku}
              onChange={(sku) => setF({ sku })}
              options={(skus.data ?? []).map((s) => ({
                value: s.id,
                label: `${s.code} · ${s.name}`,
              }))}
            />
            <SelectFilter
              label="Status"
              value={f.status as 'DRAFT' | 'APPROVED' | 'RETIRED' | ''}
              onChange={(status) => setF({ status })}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'RETIRED', label: 'Retired' },
              ]}
            />
            <SelectFilter
              label="Packing hall"
              value={f.hall}
              onChange={(hall) => setF({ hall })}
              options={(halls.data ?? []).map((h) => ({ value: h.id, label: h.name }))}
            />
          </FilterBar>
        }
      />

      <DetailDrawer
        open={Boolean(opened)}
        onClose={() => setF({ open: '' })}
        eyebrow="Label version"
        title={opened ? <Code className="text-[inherit]">{opened.code}</Code> : ''}
        meta={
          opened ? (
            <Status status={opened.status} label={labelStatusText(opened.status)} />
          ) : undefined
        }
        actions={
          opened ? (
            <div className="flex flex-wrap justify-end gap-2">{actionsFor(opened, 'md')}</div>
          ) : undefined
        }
        width="lg"
      >
        {opened && <LabelDetail l={opened} sku={skuById.get(opened.skuId)} />}
      </DetailDrawer>

      {editing && (
        <LabelDialog
          editing={editing}
          skus={skus.data ?? []}
          halls={halls.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      <ApproveLabel row={approving} onClose={() => setApproving(null)} />
      <RetireLabel row={retiring} onClose={() => setRetiring(null)} />
    </ConsolePage>
  )
}

function LabelDetail({ l, sku }: { l: LabelVersionRow; sku: SkuRow | undefined }) {
  const problems = l.status === 'DRAFT' ? problemsOf(l) : []
  return (
    <div className="flex flex-col gap-5">
      <LabelPreview data={previewOf(l, sku)} />
      {l.status === 'DRAFT' &&
        (problems.length > 0 ? (
          <Banner tone="warn" title="Before it can be approved">
            <ul className="list-disc pl-5 text-[14px] text-ink">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Banner>
        ) : (
          <Banner tone="success" title="Every line the law needs is there">
            The quality person can approve it.
          </Banner>
        ))}
      <Facts
        columns={2}
        items={[
          { label: 'Product', value: `${l.skuName} (${l.skuCode})` },
          { label: 'Packing hall', value: l.premisesName ?? l.premisesCode },
          {
            label: 'Hall FSSAI licence',
            value: l.fssaiLicenceNo ? (
              <span>
                <Code>{l.fssaiLicenceNo}</Code>
                {l.fssaiLicenceValidTo && (
                  <span className="text-muted">
                    {' '}
                    · valid to <When at={l.fssaiLicenceValidTo} dateOnly />
                  </span>
                )}
              </span>
            ) : (
              <span className="text-danger">None on file</span>
            ),
          },
          { label: 'MRP', value: <Money paise={l.mrpPaise} /> },
          {
            label: 'Test panel',
            value: l.claimPanel ? l.claimPanel.replace(/_/g, ' ').toLowerCase() : '—',
          },
          { label: 'Substances tested (at least)', value: l.claimMinAnalytes ?? '—', mono: true },
          {
            label: 'Highest detection limit',
            value: l.claimMaxLoq ? `${l.claimMaxLoq} mg/kg` : '—',
            mono: true,
          },
          {
            label: 'Artwork',
            value: l.artworkUrl ? (
              <a
                href={l.artworkUrl}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                Open artwork
              </a>
            ) : (
              '—'
            ),
          },
          {
            label: 'Approved',
            value: l.approvedAt ? (
              <span>
                <When at={l.approvedAt} /> · <UserName id={l.approvedById} />
              </span>
            ) : (
              '—'
            ),
          },
          { label: 'Added', value: <When at={l.createdAt} /> },
        ]}
      />
    </div>
  )
}

function LabelDialog({
  editing,
  skus,
  halls,
  onClose,
}: {
  editing: Editing
  skus: SkuRow[]
  halls: PackingHall[]
  onClose: () => void
}) {
  const edit = editing.mode === 'edit' ? editing.row : null
  const [d, setD] = useState<LabelDraft>(
    edit
      ? draftFromLabel(edit)
      : editing.mode === 'create' && editing.draft
        ? editing.draft
        : emptyLabelDraft(),
  )
  const [tried, setTried] = useState(false)
  const [touched, setTouched] = useState<Set<keyof LabelDraft>>(new Set())
  const save = useSaveLabel()
  const p = validateLabelDraft(d, editing.mode)
  const shown = (k: keyof LabelDraft) => (tried || touched.has(k) ? p[k] : undefined)
  const touch = (k: keyof LabelDraft) => setTouched((s) => new Set(s).add(k))
  const set = (patch: Partial<LabelDraft>) => setD((x) => ({ ...x, ...patch }))
  const sku = skus.find((s) => s.id === d.skuId)
  const hall = halls.find((h) => h.id === d.premisesLocationId)
  const claimWords = claimWordsProblem(d.claimText)
  const preview: LabelPreviewData = {
    productName: sku?.name ?? edit?.skuName ?? '',
    netG: sku?.netG ?? null,
    mrpPaise: parseRupeesToPaise(d.mrpInr),
    shelfLifeDays: sku?.shelfLifeDays ?? null,
    fssaiLicenceNo: hall?.fssaiLicenceNo ?? null,
    allergenStatement: d.allergenStatement.trim() || null,
    claimText: d.claimText.trim() || null,
    hallName: hall?.name ?? null,
  }

  return (
    <FormDialog
      open
      onClose={onClose}
      title={edit ? `Edit draft ${edit.code}` : 'New label'}
      description="A draft can be saved with lines still missing; approval needs them all."
      saveLabel={edit ? 'Save draft' : 'Save as draft'}
      loading={save.isPending}
      onSave={async () => {
        setTried(true)
        if (!labelOk(p)) throw new Error('invalid')
        await save.mutateAsync(
          edit ? { id: edit.id, body: updateLabelBody(d) } : { body: createLabelBody(d) },
        )
      }}
    >
      {save.isError && <RefusalBanner error={save.error} />}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Product" required={!edit} error={shown('skuId')}>
          {edit ? (
            <Input value={`${edit.skuCode} · ${edit.skuName}`} disabled />
          ) : (
            <Select
              value={d.skuId}
              onChange={(skuId) => set({ skuId })}
              onBlur={() => touch('skuId')}
              placeholder="Pick the product"
              options={skus.map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }))}
            />
          )}
        </Field>
        <Field
          label="Version code"
          required={!edit}
          error={shown('code')}
          hint={edit ? undefined : 'As printed on the roll, e.g. L-GN500-v4'}
        >
          <Input
            value={d.code}
            disabled={Boolean(edit)}
            onChange={(e) => set({ code: e.target.value.replace(/\s+/g, '-') })}
            onBlur={() => touch('code')}
            maxLength={20}
            className="font-mono"
          />
        </Field>
        <Field
          label="Packing hall"
          required
          error={shown('premisesLocationId')}
          hint={
            hall && !hall.fssaiLicenceNo ? 'This hall has no FSSAI licence on file yet.' : undefined
          }
        >
          <Select
            value={d.premisesLocationId}
            onChange={(premisesLocationId) => set({ premisesLocationId })}
            onBlur={() => touch('premisesLocationId')}
            placeholder="Pick the hall"
            options={halls.map((h) => ({ value: h.id, label: h.name }))}
          />
        </Field>
        <Field label="MRP" required error={shown('mrpInr')}>
          <Input
            inputMode="decimal"
            value={d.mrpInr}
            onChange={(e) => set({ mrpInr: e.target.value.replace(/[^\d.]/g, '') })}
            onBlur={() => touch('mrpInr')}
            leading="₹"
            className="font-mono"
            placeholder="180.00"
          />
        </Field>
      </div>
      <Field
        label="Allergen line"
        error={shown('allergenStatement')}
        hint="e.g. Packed in a facility that also handles sesame."
      >
        <TextArea
          rows={2}
          value={d.allergenStatement}
          maxLength={200}
          onChange={(e) => set({ allergenStatement: e.target.value })}
        />
      </Field>
      <Field
        label="Claim"
        error={claimWords ?? shown('claimText')}
        hint="Say what was tested and that it was not detected, e.g. Tested for 58 pesticides: not detected."
      >
        <TextArea
          rows={2}
          value={d.claimText}
          maxLength={300}
          onChange={(e) => set({ claimText: e.target.value })}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field
          label="Test panel"
          error={shown('claimPanel')}
          hint={`e.g. ${PANEL_HINTS.join(', ')}`}
        >
          <Input
            value={d.claimPanel}
            onChange={(e) => set({ claimPanel: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
            maxLength={40}
            className="font-mono"
            list="pack-panel-hints"
          />
          <datalist id="pack-panel-hints">
            {PANEL_HINTS.map((h) => (
              <option key={h} value={h} />
            ))}
          </datalist>
        </Field>
        <Field label="Substances tested (at least)" error={shown('claimMinAnalytes')}>
          <Input
            inputMode="numeric"
            value={d.claimMinAnalytes}
            onChange={(e) => set({ claimMinAnalytes: e.target.value.replace(/\D/g, '') })}
            className="font-mono"
            placeholder="58"
          />
        </Field>
        <Field label="Highest detection limit" error={shown('claimMaxLoq')} hint="mg/kg">
          <Input
            inputMode="decimal"
            value={d.claimMaxLoq}
            onChange={(e) => set({ claimMaxLoq: e.target.value.replace(/[^\d.]/g, '') })}
            className="font-mono"
            placeholder="0.01"
          />
        </Field>
      </div>
      <Field
        label="Artwork link"
        error={shown('artworkUrl')}
        hint="Optional: a link to the artwork file."
      >
        <Input
          value={d.artworkUrl}
          onChange={(e) => set({ artworkUrl: e.target.value })}
          maxLength={1000}
        />
      </Field>
      <div>
        <div className="mb-2 font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
          How it prints
        </div>
        <LabelPreview data={preview} />
      </div>
    </FormDialog>
  )
}

function ApproveLabel({ row, onClose }: { row: LabelVersionRow | null; onClose: () => void }) {
  const approve = useApproveLabel()
  const problems = row ? problemsOf(row) : []
  const close = () => {
    approve.reset()
    onClose()
  }
  return (
    <ConfirmDialog
      open={Boolean(row)}
      onClose={close}
      title={`Approve ${row?.code ?? ''}?`}
      description="Once approved the label is fixed. Any earlier approved version of this product at this hall is retired; lots already packed keep theirs."
      confirmLabel="Approve label"
      loading={approve.isPending}
      disabledReason={
        problems.length > 0 ? 'Fill in the missing lines first (Edit the draft).' : null
      }
      onConfirm={async () => {
        if (!row) return
        await approve.mutateAsync(row.id)
        close()
      }}
    >
      {problems.length > 0 && (
        <Banner tone="warn" title="Not ready yet">
          <ul className="list-disc pl-5 text-[14px] text-ink">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Banner>
      )}
      {approve.isError && <RefusalBanner error={approve.error} />}
    </ConfirmDialog>
  )
}

function RetireLabel({ row, onClose }: { row: LabelVersionRow | null; onClose: () => void }) {
  const retire = useRetireLabel()
  const draft = row?.status === 'DRAFT'
  return (
    <ConfirmDialog
      open={Boolean(row)}
      onClose={onClose}
      title={draft ? `Withdraw draft ${row?.code ?? ''}?` : `Retire ${row?.code ?? ''}?`}
      description={
        draft
          ? 'The draft is removed. Nothing was printed with it.'
          : 'No new pack run can start with this label. Lots already packed keep it. The product cannot be packed at this hall until another version is approved.'
      }
      confirmLabel={draft ? 'Withdraw draft' : 'Retire label'}
      tone="danger"
      loading={retire.isPending}
      onConfirm={async () => {
        if (!row) return
        await retire.mutateAsync({ id: row.id, draft })
        onClose()
      }}
    />
  )
}
